import { useEffect, useState } from 'react';

/**
 * useModalLeave — page-level modal 의 unmount transition 표준 hook.
 *
 * adr-20260506-modal-unmount-transition-css-only-pattern §2.1 시그니처의 latched
 * 변형. 부모의 `{cond && <Modal {...} />}` 패턴에서 close 시 cond=null 로 인해
 * dependent prop 이 stale 되는 문제를 latched value 로 봉합.
 *
 * 사용 (CalendarPage 예):
 *   const { shouldRender, isLeaving, latched } = useModalLeave(selectedEvent, 200);
 *   {shouldRender && latched && (
 *     <EventDetailModal event={latched} isLeaving={isLeaving} onClose={...} />
 *   )}
 *
 * durationMs 는 tokens.css 의 --duration-base (200ms) 와 동기 유지. token scale 변경
 * 시 hook default 도 동기 변경 필요 (Risk R3).
 */

export interface UseModalLeaveResult<T> {
  /** modal 을 DOM 에 렌더할지 여부 (mount + leaving 모두 true). */
  shouldRender: boolean;
  /** leaving transition 진행 중 여부 (CSS --leaving 클래스 토글용). */
  isLeaving: boolean;
  /** 마지막 open 시점의 값 — leaving 중에도 보존되어 stale prop 문제 봉합. */
  latched: T | null;
}

export function useModalLeave<T>(
  open: T | null | false | undefined,
  durationMs: number = 200,
): UseModalLeaveResult<T> {
  const isOpen = open !== null && open !== false && open !== undefined;
  const [shouldRender, setShouldRender] = useState<boolean>(isOpen);
  const [isLeaving, setIsLeaving] = useState<boolean>(false);
  const [latched, setLatched] = useState<T | null>(isOpen ? (open as T) : null);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsLeaving(false);
      setLatched(open as T);
      return;
    }
    if (!shouldRender) return;
    setIsLeaving(true);
    const t = setTimeout(() => {
      setShouldRender(false);
      setIsLeaving(false);
      setLatched(null);
    }, durationMs);
    return () => clearTimeout(t);
  }, [isOpen, open, durationMs, shouldRender]);

  return { shouldRender, isLeaving, latched };
}
